import type { PatternFormField, PatternFormFieldItem } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { isNotNull } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { Pattern } from '~/common/model/api/batch-type/pattern';
import type { ProcessFlowStepConnectorRcipParamGroup, ProcessFlowStepConnectorRcipParams } from '~/common/model/api/batch-type/process-flow';

import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

import type { ProcessFlowStepConnectorRcipParamGroupFormField, ProcessFlowStepConnectorRcipParamsFormField } from './_rcip.type';

const logger = createLogger('logic/feature/settings/batch-type-library');

// * Transformation to text is not necessary
const IS_TRANSFORMATION_TEXT = false;

/**
 * Create FieldPattern from FieldPattern FormField.
 *
 * @param {PatternFormField} formField FieldPattern FormField.
 *
 * @returns {Pattern[]} FieldPattern.
 */
export function createFieldPattern(formField: PatternFormField): Pattern[] {
  const scope: PatternAccessScope = 'document';

  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (typeof item === 'string') {
      pattern = patternLogic.createPattern.fromText(item);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern = patternLogic.createPattern.fromSystemValue(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchBusinessRule': {
          pattern = patternLogic.createPattern.fromBusinessRule(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentBusinessRule': {
          pattern = patternLogic.createPattern.fromDocumentBusinessRule(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'parameter': {
          pattern = patternLogic.createPattern.fromParameter(item.value as string);
          break;
        }

        case 'output': {
          pattern = patternLogic.createPattern.fromOutput(item.value as string);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create FieldPattern FormField from FieldPattern.
 *
 * @param {Pattern[]} fieldPattern FieldPattern.
 *
 * @returns {PatternFormField} FieldPattern FormField.
 */
export function createFieldPatternFormField(fieldPattern: Pattern[]): PatternFormField {
  return fieldPattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormFieldItem>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      case 'parameter': {
        item = patternLogic.createPatternFormFieldItem.fromPatternParameter(pattern);
        break;
      }

      case 'output': {
        item = patternLogic.createPatternFormFieldItem.fromPatternOutput(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}

/**
 * Create ProcessFlow Step Connector RCIP Params form RCIP Params FormField.
 *
 * @param {ProcessFlowStepConnectorRcipParamsFormField} paramsFormField RCIP Params FormField.
 *
 * @returns {ProcessFlowStepConnectorRcipParams} RCIP Params.
 */
export function createParams(paramsFormField: ProcessFlowStepConnectorRcipParamsFormField): ProcessFlowStepConnectorRcipParams {
  return Object.entries(paramsFormField).reduce<ProcessFlowStepConnectorRcipParams>((prev, [id, param]) => {
    if ('params' in param) {
      const group: ProcessFlowStepConnectorRcipParamGroup = {
        params: createParams(param.params),
      };

      return {
        ...prev,
        [id]: group,
      };
    }

    return {
      ...prev,
      [id]: createFieldPattern(param),
    };
  }, {});
}

/**
 * Create RCIP Params FormField from RCIP Params.
 *
 * @param {ProcessFlowStepConnectorRcipParams} params RCIP Params.
 *
 * @returns {ProcessFlowStepConnectorRcipParamsFormField} RCIP Params FormField.
 */
export function createParamsFormField(params: ProcessFlowStepConnectorRcipParams): ProcessFlowStepConnectorRcipParamsFormField {
  return Object.entries(params).reduce<ProcessFlowStepConnectorRcipParamsFormField>((prev, [id, param]) => {
    if ('params' in param) {
      const group: ProcessFlowStepConnectorRcipParamGroupFormField = {
        params: createParamsFormField(param.params),
      };

      return {
        ...prev,
        [id]: group,
      };
    }

    return {
      ...prev,
      [id]: createFieldPatternFormField(param),
    };
  }, {});
}
